import { BaseService } from './base';
import { ApiRequest } from '../lib/request';
import type { ApiResponse, ApiListResponse } from '../lib/response';
import {
  Booking,
  BookingDates,
  BookingStats,
  BookingValidation,
  BookingDefaults,
  AvailabilityRequest,
  AvailabilityResponse,
  ConfirmationRequest,
} from '../models/booking';
import { Reservation } from '../models/reservation';
import { Employee } from '../models/employee';
import { Service } from '../models/service';
import { RecurrencePreview } from '../models/recurrence';

export class BookingService extends BaseService<Booking> {
  override model = Booking;

  override modelName = 'booking';
  override modelLabel = 'Bookings';

  // Static API Methods

  async day(date: string): Promise<ApiResponse<Booking>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/day`,
      query: { date },
    });

    return await this.requestItem<Booking>(request, Booking);
  }

  async employee(employeeId: string, date: string): Promise<ApiResponse<Booking>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${employeeId}/employee`,
      query: { date },
    });

    return await this.requestItem<Booking>(request, Booking);
  }

  async reservations(
    dates: string[],
    employeeIds: string[]
  ): Promise<ApiResponse<BookingDates>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/dates`,
      data: {
        dates,
        employees: employeeIds,
      },
    });

    return await this.requestItem<BookingDates>(request, BookingDates);
  }

  async timetable(
    dates: string[],
    employeeIds: string[]
  ): Promise<ApiResponse<BookingDates>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/timetable`,
      data: {
        dates,
        employees: employeeIds,
      },
    });

    return await this.requestItem<BookingDates>(request, BookingDates);
  }

  async stats(
    dates: string[],
    employeeIds: string[]
  ): Promise<ApiResponse<BookingStats>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/stats`,
      data: {
        dates,
        employees: employeeIds,
      },
    });

    return await this.requestItem<BookingStats>(request, BookingStats);
  }

  async upcoming(
    { serviceId, idService, dates }: { serviceId?: string | number; idService?: string | number; dates?: string[] } = {}
  ): Promise<ApiListResponse<Reservation>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/upcoming`,
      data: {
        idService: serviceId ?? idService,
        dates,
      },
    });

    return await this.requestList<Reservation>(request, Reservation);
  }

  async availability(
    availabilityRequest: AvailabilityRequest
  ): Promise<ApiResponse<AvailabilityResponse>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/availability`,
      data: availabilityRequest.toJSON(),
    });

    return await this.requestItem<AvailabilityResponse>(request, AvailabilityResponse);
  }

  async confirm(
    confirmationRequest: ConfirmationRequest,
    { overrideTime = false, sendEmail = true }: { overrideTime?: boolean; sendEmail?: boolean } = {}
  ): Promise<ApiResponse<Reservation>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/confirm`,
      query: {
        overrideTime: overrideTime ? '1' : '0',
        sendEmail: sendEmail ? '1' : '0',
      },
      data: confirmationRequest.toJSON(),
    });

    return await this.requestItem<Reservation>(request, Reservation);
  }

  async confirmInfo(
    confirmationRequest: ConfirmationRequest
  ): Promise<ApiResponse<BookingValidation>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/confirmInfo`,
      data: confirmationRequest.toJSON(),
    });

    return await this.requestItem<BookingValidation>(request, BookingValidation);
  }

  async move(
    confirmationRequest: ConfirmationRequest,
    {
      overrideTime = false,
      sendEmail = true,
      resetReminder = true,
      future = false,
    }: {
      overrideTime?: boolean;
      sendEmail?: boolean;
      resetReminder?: boolean;
      future?: boolean;
    } = {}
  ): Promise<ApiResponse<Reservation>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/move`,
      query: {
        future: future ? '1' : '0',
        overrideTime: overrideTime ? '1' : '0',
        sendEmail: sendEmail ? '1' : '0',
        resetReminder: resetReminder ? '1' : '0',
      },
      data: confirmationRequest.toJSON(),
    });

    return await this.requestItem<Reservation>(request, Reservation);
  }

  async bookingDefaults(): Promise<ApiResponse<BookingDefaults>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/bookingDefaults`,
    });

    return await this.requestItem<BookingDefaults>(request, BookingDefaults);
  }

  async services(
    { modifiedSince, signal, accountId }: { modifiedSince?: Date; signal?: AbortSignal; accountId?: string } = {}
  ): Promise<ApiListResponse<Service>> {
    const headers: Record<string, string> = {};
    if (accountId) headers['X-Ovatu-Account'] = accountId;
    if (modifiedSince) {
      headers['If-Modified-Since'] = modifiedSince.toUTCString();
    }

    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/services`,
      headers,
      signal,
    });

    return await this.requestList<Service>(request, Service);
  }

  async recurrence(
    reservationId: string,
    recurrenceRequest: any
  ): Promise<ApiResponse<Reservation>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${reservationId}/recurrence`,
      data: recurrenceRequest.toJSON?.() ?? recurrenceRequest,
    });

    return await this.requestItem<Reservation>(request, Reservation);
  }

  async previewRecurrence(
    recurrenceRequest: any
  ): Promise<ApiResponse<RecurrencePreview>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/previewRecurrence`,
      data: recurrenceRequest.toJSON?.() ?? recurrenceRequest,
    });

    return await this.requestItem<RecurrencePreview>(request, RecurrencePreview);
  }

  async availableEmployees({
    employeeIds = [],
    date,
    startTime,
    duration,
    processing,
    finishing,
    preBuffer,
    postBuffer,
    excludeReservationIds = [],
    excludeReservationCustomerIds = [],
  }: {
    employeeIds?: string[];
    date: string;
    startTime: number;
    duration: number;
    processing: number;
    finishing: number;
    preBuffer: number;
    postBuffer: number;
    excludeReservationIds?: string[];
    excludeReservationCustomerIds?: string[];
  }): Promise<ApiListResponse<Employee>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/availableEmployees`,
      data: {
        employees: employeeIds,
        date,
        startTime,
        duration,
        processing,
        finishing,
        preBuffer,
        postBuffer,
        excludeReservations: excludeReservationIds,
        excludeReservationCustomers: excludeReservationCustomerIds,
      },
    });

    return await this.requestList<Employee>(request, Employee);
  }
}
